// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/NewsSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2, FiX, FiImage } from 'react-icons/fi';
import api from '../../../api';

const NewsSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const news = websiteData?.news || { items: [] };

  // Local configurations to insulate typing focus changes completely from parent cycles
  const [localTitle, setLocalTitle] = useState(news.sectionTitle || '');
  const [localSubtitle, setLocalSubtitle] = useState(news.sectionSubtitle || '');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(news.displayStyle || 'grid');
  const [localCheckboxes, setLocalCheckboxes] = useState({
    showDates: news.showDates !== false,
    showPublicationLogos: news.showPublicationLogos !== false
  });
  const [localItems, setLocalItems] = useState(news.items || []);

  const [newItem, setNewItem] = useState({
    title: '',
    publication: '',
    date: '',
    excerpt: '',
    link: ''
  });
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);

  // Sync isolated state fields from parent only on clean mount or tenant change parameters
  useEffect(() => {
    console.log('[NewsSection] Setting up local typing variables from base parent properties snapshot.');
    const sec = websiteData?.news || {};
    setLocalTitle(sec.sectionTitle || '');
    setLocalSubtitle(sec.sectionSubtitle || '');
    setLocalDisplayStyle(sec.displayStyle || 'grid');
    setLocalCheckboxes({
      showDates: sec.showDates !== false,
      showPublicationLogos: sec.showPublicationLogos !== false
    });
    setLocalItems(sec.items || []);
  }, [websiteData?.news, tenantId]); // Fixed missing dependencies warning explicitly

  const handleToggle = () => {
    handleInputChange('news', 'isActive', !news.isActive);
  };

  const handleGlobalFieldChange = (field, value) => {
    console.log(`[NewsSection] Local global configuration altered: Key [${field}] -> Value [${value}]`);
    if (field === 'sectionTitle') setLocalTitle(value);
    if (field === 'sectionSubtitle') setLocalSubtitle(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
  };

  const handleLocalCheckboxToggle = (key) => {
    setLocalCheckboxes(prev => {
      const nextState = { ...prev, [key]: !prev[key] };
      console.log(`[NewsSection] Adjusting configuration switch [${key}]:`, nextState[key]);
      return nextState;
    });
  };

  const handleAddItem = () => {
    if (!newItem.title || !newItem.publication) return;
    
    const item = {
      _id: Date.now().toString(),
      title: newItem.title,
      publication: newItem.publication,
      date: newItem.date,
      excerpt: newItem.excerpt,
      link: newItem.link,
      isFeatured: false,
      publicationLogo: ''
    };
    
    const updatedItems = [...localItems, item];
    console.log('[NewsSection] Appending a new article card locally:', item);
    setLocalItems(updatedItems);
    
    // Sync array changes instantly up to protect layout mapping list logic definitions
    handleInputChange('news', 'items', updatedItems);
    setNewItem({ title: '', publication: '', date: '', excerpt: '', link: '' });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = localItems.filter((_, i) => i !== index);
    console.log('[NewsSection] Removing article index position context:', index);
    setLocalItems(updatedItems);
    
    // Sync array changes instantly up to protect layout mapping list logic definitions
    handleInputChange('news', 'items', updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    console.log(`[NewsSection] Modifying item data grid details -> Row [${index}] -> Field [${field}] -> Value [${value}]`);
    setLocalItems(updatedItems);
  };

  const handleLogoUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 1 * 1024 * 1024) {
      alert('Image size must be less than 1MB');
      event.target.value = '';
      return;
    }

    const localBlobPreview = URL.createObjectURL(file);
    const previewItems = [...localItems];
    previewItems[index] = { ...previewItems[index], publicationLogo: localBlobPreview };
    setLocalItems(previewItems);

    setUploading(prev => ({ ...prev, [index]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'news');
      formData.append('itemIndex', index);
      formData.append('subField', 'publicationLogo');

      const response = await api.post(`/api/website/${tenantId}/upload/news`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const fileUrl = response.data.data?.url || response.data.data;
        console.log('[NewsSection] Asset uploaded successfully path string location resolved:', fileUrl);
        const updatedItems = [...localItems];
        updatedItems[index] = { ...updatedItems[index], publicationLogo: fileUrl };
        setLocalItems(updatedItems);
        handleInputChange('news', 'items', updatedItems);
      } else {
        alert(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(prev => ({ ...prev, [index]: false }));
      event.target.value = '';
    }
  };

  const resolveAssetUrl = (urlPath) => {
    if (!urlPath) return '';
    if (typeof urlPath === 'string' && (urlPath.includes('tenants%2Fnull') || urlPath.includes('tenants/null'))) {
      return '';
    }
    const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '') || (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (typeof urlPath === 'object') {
      if (urlPath.url) return urlPath.url.startsWith('/') ? (base ? `${base}${urlPath.url}` : urlPath.url) : urlPath.url;
      if (urlPath.fileId) return base ? `${base}/api/files/tenant-website/${urlPath.fileId}` : `/api/files/tenant-website/${urlPath.fileId}`;
      return '';
    }
    if (/^https?:\/\//i.test(urlPath)) return urlPath;
    if (urlPath.startsWith('/')) return base ? `${base}${urlPath}` : urlPath;
    return urlPath;
  };

  const handleRemoveLogo = (index) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], publicationLogo: '' };
    setLocalItems(updatedItems);
    handleInputChange('news', 'items', updatedItems);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[NewsSection] Pushing final structured parameters up into standard websiteData context blocks.');
    
    try {
      // Synchronously push local parameters data up to master context tree configurations map
      await handleInputChange('news', 'sectionTitle', localTitle);
      await handleInputChange('news', 'sectionSubtitle', localSubtitle);
      await handleInputChange('news', 'displayStyle', localDisplayStyle);
      await handleInputChange('news', 'showDates', localCheckboxes.showDates);
      await handleInputChange('news', 'showPublicationLogos', localCheckboxes.showPublicationLogos);
      await handleInputChange('news', 'items', localItems);

      const assembledPayload = {
        ...news,
        sectionTitle: localTitle,
        sectionSubtitle: localSubtitle,
        displayStyle: localDisplayStyle,
        showDates: localCheckboxes.showDates,
        showPublicationLogos: localCheckboxes.showPublicationLogos,
        items: localItems
      };

      await handleSave('news', assembledPayload);
    } catch (error) {
      console.error('Failed to save news section:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Top Header Configuration Layout Card Container */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">News & Media</h2>
            <p className="text-sm text-slate-500 mt-0.5">Showcase external press media mentions and structural news coverage items</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              type="button"
              onClick={handleToggle}
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                news.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {news.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleSaveClick}
              disabled={saving}
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Isolated Input Layout fields card */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Section Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleGlobalFieldChange('sectionTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none animate-none"
                placeholder="In the News"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleGlobalFieldChange('sectionSubtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Making headlines"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Alignment</label>
              <select
                value={localDisplayStyle}
                onChange={(e) => handleGlobalFieldChange('displayStyle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
              >
                <option value="grid">Normal Grid Matrix</option>
                <option value="list">Vertical Row List</option>
              </select>
            </div>

            <div className="md:col-span-2 bg-slate-50 rounded-xl p-3 border border-slate-150 grid grid-cols-2 gap-3">
              {[
                { label: 'Show Dates', key: 'showDates', checked: localCheckboxes.showDates },
                { label: 'Show Publication Logos', key: 'showPublicationLogos', checked: localCheckboxes.showPublicationLogos },
              ].map((checkbox) => (
                <label key={checkbox.key} className="flex items-center space-x-2.5 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={checkbox.checked}
                    onChange={() => handleLocalCheckboxToggle(checkbox.key)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                    checkbox.checked 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-sm bg-white ${checkbox.checked ? 'block' : 'hidden'}`} />
                  </div>
                  <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {checkbox.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Dynamic Mapping items wrapper loop card */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-bold text-slate-800">News Items</h3>
            <span className="text-xs font-semibold bg-slate-200 text-slate-600 px-2.5 py-0.5 rounded-full">
              {localItems.length} Entries Added
            </span>
          </div>

          <div className="space-y-4">
            {localItems.map((item, index) => {
              const resolvedLogo = resolveAssetUrl(item.publicationLogo);

              return (
                <div key={item._id || index} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                  <div className="p-4 sm:p-5 space-y-4">
                    
                    {/* Form input configuration alignments */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Headline Article Title</span>
                        <input
                          type="text"
                          value={item.title || ''}
                          onChange={(e) => handleItemChange(index, 'title', e.target.value)}
                          placeholder="Headline Title"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Publication Venue Name</span>
                        <input
                          type="text"
                          value={item.publication || ''}
                          onChange={(e) => handleItemChange(index, 'publication', e.target.value)}
                          placeholder="Media Outlet Name"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Release Coverage Date</span>
                        <input
                          type="date"
                          value={item.date || ''}
                          onChange={(e) => handleItemChange(index, 'date', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Short Article Excerpt Abstract</span>
                        <textarea
                          value={item.excerpt || ''}
                          onChange={(e) => handleItemChange(index, 'excerpt', e.target.value)}
                          placeholder="Short article coverage abstract summary details..."
                          rows="2"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500 resize-y"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Full Destination Article URL</span>
                        <input
                          type="text"
                          value={item.link || ''}
                          onChange={(e) => handleItemChange(index, 'link', e.target.value)}
                          placeholder="Link URL to full destination article cover"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Logo Image Picker Section Frame Container layout layout */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-slate-50 p-3 rounded-xl border border-slate-150">
                      <div className="flex-shrink-0 mx-auto sm:mx-0">
                        <div className="w-16 h-16 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden bg-white shadow-inner relative group">
                          {resolvedLogo ? (
                            <div className="w-full h-full p-1 relative">
                              <img 
                                src={resolvedLogo} 
                                alt={item.publication || "Logo"} 
                                className="w-full h-full object-contain"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveLogo(index)}
                                className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white rounded-xl"
                              >
                                <FiX className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <FiImage className="h-6 w-6 text-slate-400" />
                          )}
                        </div>
                      </div>
                      <div className="text-center sm:text-left flex-1">
                        <input
                          type="file"
                          id={`news-logo-${index}`}
                          accept="image/jpeg,image/png,image/webp,image/svg+xml"
                          onChange={(e) => handleLogoUpload(e, index)}
                          disabled={uploading[index]}
                          className="hidden"
                        />
                        <label
                          htmlFor={`news-logo-${index}`}
                          className={`inline-flex items-center px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 shadow-sm cursor-pointer transition-all select-none ${
                            uploading[index] ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                          }`}
                        >
                          {uploading[index] ? 'Uploading...' : (resolvedLogo ? 'Change Publication Logo' : 'Upload Publication Logo')}
                        </label>
                        <p className="text-[10px] text-slate-400 mt-1">Accepts JPG, PNG, WEBP, SVG under 1MB</p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2 border-t border-slate-100">
                      <label className="flex items-center space-x-2 cursor-pointer group select-none">
                        <input
                          type="checkbox"
                          checked={item.isFeatured || false}
                          onChange={(e) => handleItemChange(index, 'isFeatured', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${
                          item.isFeatured ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-xs bg-white ${item.isFeatured ? 'block' : 'hidden'}`} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-800">Featured Highlight Entry</span>
                      </label>

                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-rose-500 hover:text-rose-700 font-semibold text-xs flex items-center px-2 py-1 rounded-md hover:bg-rose-50 transition-colors"
                      >
                        <FiTrash2 className="mr-1 h-3.5 w-3.5" />
                        Delete Coverage
                      </button>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

          {/* Add Item Action Form alignments */}
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm mt-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1 bg-blue-50 rounded-md text-blue-600">
                <FiPlus className="h-4 w-4 stroke-[3]" />
              </div>
              <h4 className="text-sm font-bold text-slate-800">Add New Media Coverage</h4>
            </div>

            <div className="space-y-3">
              <input
                type="text"
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                placeholder="Headline Title Headline *"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={newItem.publication}
                  onChange={(e) => setNewItem({ ...newItem, publication: e.target.value })}
                  placeholder="Publication Venue Name *"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                />
                <input
                  type="date"
                  value={newItem.date}
                  onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-end gap-3">
                <textarea
                  value={newItem.excerpt}
                  onChange={(e) => setNewItem({ ...newItem, excerpt: e.target.value })}
                  placeholder="Brief press summary clip details"
                  rows="2"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 resize-y"
                />
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 bg-slate-900 text-white font-semibold rounded-lg text-sm hover:bg-slate-800 transition-all active:scale-[0.98] shadow-sm whitespace-nowrap"
                >
                  Insert Coverage
                </button>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default NewsSection;